import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Zap } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import useAssessmentStore from '../hooks/useAssessment';
import { QUESTIONS, DIMENSIONS, MICRO_INSIGHT_INDICES, getDimension } from '../lib/questions';
import { generateMicroInsight, parseInsightText } from '../lib/insights';
import { calculateFullResults } from '../lib/scoring';

const AssessmentFlow = () => {
  const navigate = useNavigate();
  const {
    userData,
    answers,
    currentQuestion,
    setAnswer,
    nextQuestion,
    previousQuestion,
    completeAssessment
  } = useAssessmentStore();

  const [showInsight, setShowInsight] = useState(false);
  const [currentInsight, setCurrentInsight] = useState(null);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);

  // Redirect if no user data
  useEffect(() => {
    if (!userData) {
      navigate('/assess');
    }
  }, [userData, navigate]);

  const question = QUESTIONS[currentQuestion];
  const dimension = question ? getDimension(question.dimensionId) : null;
  const selectedOption = answers[currentQuestion];
  const progress = ((currentQuestion + 1) / 20) * 100;

  // Check for micro-insight display
  useEffect(() => {
    const questionNumber = currentQuestion + 1;
    if (MICRO_INSIGHT_INDICES.includes(questionNumber)) {
      const insight = generateMicroInsight(answers, currentQuestion);
      setCurrentInsight(insight);
      setShowInsight(true);
    } else {
      setShowInsight(false);
      setCurrentInsight(null);
    }
  }, [currentQuestion, answers]);

  const handleOptionSelect = useCallback((optionIndex) => {
    setAnswer(currentQuestion, optionIndex);
    setIsAutoAdvancing(true);

    // Auto-advance after 400ms
    setTimeout(() => {
      if (currentQuestion === 19) {
        // Last question - calculate results and navigate
        const results = calculateFullResults({ ...answers, [currentQuestion]: optionIndex });
        completeAssessment(results);
        navigate('/results');
      } else {
        nextQuestion();
      }
      setIsAutoAdvancing(false);
    }, 400);
  }, [currentQuestion, answers, setAnswer, nextQuestion, completeAssessment, navigate]);

  const handleBack = () => {
    if (currentQuestion > 0) {
      previousQuestion();
    }
  };

  const handleNext = () => {
    if (selectedOption !== undefined && currentQuestion < 19) {
      nextQuestion();
    }
  };

  // Get dimension icon
  const getDimensionIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className="w-4 h-4" strokeWidth={1.5} /> : null;
  };

  if (!question || !dimension) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Progress */}
      <header className="px-6 md:px-12 lg:px-24 py-6 border-b border-zinc-100">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBack}
            disabled={currentQuestion === 0}
            className="flex items-center gap-2 text-zinc-500 hover:text-zinc-950 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="back-question-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
          <span className="font-serif text-xl tracking-tight">Unpaved</span>
          <span className="text-sm text-zinc-500">
            {currentQuestion + 1} of 20
          </span>
        </div>

        {/* Progress Bar */}
        <div className="h-1 w-full bg-zinc-100 overflow-hidden">
          <motion.div
            className="h-full bg-zinc-950"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>

        {/* Dimension Badge */}
        <div className="mt-4 flex justify-center">
          <div 
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-200 text-sm"
            style={{ borderColor: dimension.color, color: dimension.color }}
          >
            {getDimensionIcon(dimension.icon)}
            <span className="font-medium">{dimension.name}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 md:px-12 lg:px-24 py-8">
        <div className="w-full max-w-2xl">
          {/* Micro Insight Card */}
          <AnimatePresence mode="wait">
            {showInsight && currentInsight && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mb-8 p-6 bg-zinc-50 border-l-4 border-zinc-950"
                data-testid="micro-insight-card"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                  <span className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500">
                    {currentInsight.stat ? `Research Insight • ${currentInsight.stat}` : 'Early Insight'}
                  </span>
                </div>
                <p className="text-sm text-zinc-700 leading-relaxed">
                  {parseInsightText(currentInsight.text).map((part, i) => (
                    part.type === 'bold' ? (
                      <strong key={i} className="font-semibold text-zinc-900">{part.content}</strong>
                    ) : (
                      <span key={i}>{part.content}</span>
                    )
                  ))}
                </p>
                {currentInsight.source && (
                  <p className="text-xs text-zinc-400 mt-3 italic">— {currentInsight.source}</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Question Number & Text */}
              <div className="mb-8">
                <span className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-4 block">
                  Question {currentQuestion + 1}
                </span>
                <h2 
                  className="font-serif text-2xl sm:text-3xl tracking-tight leading-snug"
                  data-testid="question-text"
                >
                  {question.text}
                </h2>
              </div>

              {/* Options */}
              <div className="space-y-3">
                {question.options.map((option, index) => (
                  <motion.button
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => !isAutoAdvancing && handleOptionSelect(index)}
                    disabled={isAutoAdvancing}
                    data-testid={`option-${index}`}
                    className={`w-full text-left p-5 border transition-all duration-200 ${
                      selectedOption === index
                        ? 'border-zinc-950 bg-zinc-50'
                        : 'border-zinc-200 hover:border-zinc-400 bg-white'
                    } ${isAutoAdvancing ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                        selectedOption === index
                          ? 'border-zinc-950 bg-zinc-950'
                          : 'border-zinc-300'
                      }`}>
                        {selectedOption === index && (
                          <div className="w-2 h-2 rounded-full bg-white" />
                        )}
                      </div>
                      <span className="text-zinc-700 leading-relaxed">{option.text}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation (optional manual next) */}
          {selectedOption !== undefined && !isAutoAdvancing && currentQuestion < 19 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 flex justify-end"
            >
              <button
                onClick={handleNext}
                className="flex items-center gap-2 text-zinc-500 hover:text-zinc-950 transition-colors"
                data-testid="next-question-btn"
              >
                <span className="text-sm">Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AssessmentFlow;
